import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import { leaderboards } from "src/api/poker";
import Hero from "src/components/Hero/Hero";
import EventFinished from "src/components/EventFinished";
import NavTabs from "src/components/Poker/Tournaments/NavTabs";
import TitleSection from "src/components/TitleSection/TitleSection";
import SubTitleSection from "src/components/SubTitleSection/SubTitleSection";
import DynamicGoogleFormSubmitter from "src/components/Form/DynamicGoogleFormSubmitter";
import SearchablePaginatedTable from "src/components/Table/SearchablePaginatedTable";
import TournamentPlayerSelectorSection from "src/pages/poker/tournaments/TournamentPlayerSelectorSection";

const sortPlayersByDisplayOrder = (players = []) =>
  players
    .map((player, index) => ({ player, index }))
    .sort((a, b) => {
      const orderA = a.player?.displayOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.player?.displayOrder ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB || a.index - b.index;
    })
    .map(({ player }) => player);

const CreatorGoogleFormSection = ({
  title,
  text,
  formId,
  sheetName,
  promoSourceRef,
  formTextKey = "schedule",
  formFields = [],
  syncedFieldId,
  syncedOptions,
  syncedFieldValue,
  onSyncedFieldChange,
  disabled,
}) => {
  const enhancedFormFields = useMemo(
    () =>
      formFields.map((field) =>
        syncedFieldId && field?.id === syncedFieldId
          ? { ...field, type: "select", options: syncedOptions || [] }
          : field,
      ),
    [formFields, syncedFieldId, syncedOptions],
  );

  const prefilledValues = useMemo(() => {
    if (!syncedFieldId || !syncedFieldValue) return undefined;
    return { [syncedFieldId]: syncedFieldValue };
  }, [syncedFieldId, syncedFieldValue]);

  const handleFieldChange = (fieldId, value) => {
    if (
      syncedFieldId &&
      fieldId === syncedFieldId &&
      onSyncedFieldChange &&
      value !== syncedFieldValue
    ) {
      onSyncedFieldChange(value);
    }
  };

  return (
    <CreatorFormSection className="creator-form-section">
      {(title || text) && (
        <TitleSection
          dark={false}
          as="h3"
          className="creator-form-title-section"
          title={title || ""}
          text={text || ""}
        />
      )}
      <CreatorFormWrap className="creator-form-wrap">
        <DynamicGoogleFormSubmitter
          formId={formId}
          sheetName={sheetName}
          promoSourceRef={promoSourceRef}
          formTextKey={formTextKey}
          titles={false}
          formFields={enhancedFormFields}
          values={prefilledValues}
          onFieldChange={handleFieldChange}
          disabled={disabled}
        />
      </CreatorFormWrap>
    </CreatorFormSection>
  );
};

const CreatorLeaderboardShowcaseSection = ({
  leaderboardKey,
  headers,
  searchKey = "player_name",
  itemsPerPage = 25,
  rowLimit,
  disclaimers = [],
  scoringInfoTitle,
  scoringInfoText,
}) => {
  const [leaderboardData, setLeaderboardData] = useState(null);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      if (!leaderboardKey) return;
      try {
        const data = await leaderboards(leaderboardKey);
        setLeaderboardData(data);
      } catch (fetchError) {
        console.error("Failed to load leaderboard:", fetchError);
      }
    };
    fetchLeaderboard();
  }, [leaderboardKey]);

  const resolvedHeaders = useMemo(() => {
    if (Array.isArray(headers) && headers.length > 0) return headers;
    return leaderboardData?.headers || [];
  }, [headers, leaderboardData?.headers]);

  return (
    <CreatorTableContainer>
      <SearchablePaginatedTable
        headers={resolvedHeaders}
        data={leaderboardData?.rows || []}
        searchKeys={[searchKey]}
        itemsPerPage={itemsPerPage}
        rowLimit={rowLimit}
      />

      {Array.isArray(disclaimers) && disclaimers.length > 0 && (
        <CreatorRules>
          {disclaimers.map((rule, index) => (
            <ReactMarkdown key={`disclaimer-${index}`}>{rule}</ReactMarkdown>
          ))}
        </CreatorRules>
      )}

      {(scoringInfoTitle || scoringInfoText) && (
        <TitleSection title={scoringInfoTitle} text={scoringInfoText} />
      )}
    </CreatorTableContainer>
  );
};

const WsopcPlaygroundMay2026CreatorChampionship = () => {
  const { t } = useTranslation();
  const contentKey = "wsopcPlaygroundMay2026CreatorChampionship";

  const content = t(contentKey, {
    returnObjects: true,
  });

  const tabs = content?.tabs || [];
  const tabContent = content?.tabContent || [];
  const tabKeys = tabs
    .filter(tab => tab?.url?.startsWith('#'))
    .map((tab, index) =>
      (tab?.url || `#tab-${index}`).replace(/^#/, ""),
    );
  const firstTabKey = tabKeys[0] || "vote-win";
  const normalizeTabKey = useCallback(
    (key) => (key && tabKeys.includes(key) ? key : firstTabKey),
    [tabKeys, firstTabKey],
  );

  const contenderOptions = useMemo(() => {
    const allSections = tabContent.flatMap((tab) => tab?.sections || []);
    const playerSelectorSection = allSections.find(
      (section) => section?.type === "PlayerSelectorSection",
    );
    const players = sortPlayersByDisplayOrder(
      playerSelectorSection?.props?.players,
    );

    return players
      .map((player, index) => {
        const value = (player?.name || player?.nickname || "").trim();
        if (!value) return null;
        // Filter out placeholder names
        if (value === "A determiner" || value === "TBD" || value === "To be determined") return null;
        return {
          value,
          label: value,
          order: index,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.order - b.order)
      .map(({ value, label }) => ({ value, label }));
  }, [tabContent]);

  const [selectedContender, setSelectedContender] = useState(
    contenderOptions[0]?.value || "",
  );

  const updateSelectedContender = useCallback((nextContender) => {
    setSelectedContender((previousContender) => {
      if (previousContender === nextContender) {
        return previousContender;
      }
      return nextContender;
    });
  }, []);

  useEffect(() => {
    if (!contenderOptions.length) {
      if (selectedContender) setSelectedContender("");
      return;
    }

    const hasSelected = contenderOptions.some(
      (option) => option.value === selectedContender,
    );

    if (!hasSelected) {
      setSelectedContender(contenderOptions[0].value);
    }
  }, [contenderOptions, selectedContender]);

  const [activeTab, setActiveTab] = useState("vote-win");

  useEffect(() => {
    if (!tabs.length) {
      return;
    }

    const handleHashChange = () => {
      const hash = window.location.hash?.slice(1);
      if (hash && tabKeys.includes(hash)) {
        setActiveTab(hash);
      } else if (!hash) {
        setActiveTab("vote-win");
      }
    };

    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);

    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [tabs, tabKeys]);

  if (!content || !tabs.length) {
    return null;
  }

  const handleTabSelect = (key) => {
    if (!key) return;
    setActiveTab(key);
    window.history.replaceState(null, "", `#${key}`);
  };

  const renderSection = (section, index) => {
    const { type, props = {} } = section || {};

    const componentMap = {
      TitleSection: TitleSection,
      SubTitleSection: SubTitleSection,
      TextContent: ({ content: markdown, variant }) => (
        <ReactMarkdown>{markdown}</ReactMarkdown>
      ),
      PlayerSelectorSection: TournamentPlayerSelectorSection,
      GoogleFormSection: CreatorGoogleFormSection,
      LeaderboardShowcaseSection: CreatorLeaderboardShowcaseSection,
    };

    const Component = componentMap[type];

    if (!Component) {
      console.warn(`Component type "${type}" not supported in ${contentKey}`);
      return null;
    }

    const mergedProps = {
      ...props,
      selectedContender,
      onContenderChange: updateSelectedContender,
      syncedFieldValue: selectedContender,
      syncedOptions: contenderOptions,
      onSyncedFieldChange: updateSelectedContender,
      // Override formId and disabled from locale with inline values
      ...(type === "GoogleFormSection" && {
        formId: "AKfycbwhROPDvo3isBiDYX69CvQWUwsX4h9NBqn45OcWTEWP-I5J1uIArHhdzM6Whne4nvkPgQ",
        disabled: false
      }),
      ...(type === "PlayerSelectorSection" && {
        disabled: false,
        players: sortPlayersByDisplayOrder(props.players),
      }),
    };

    const rendered = <Component key={`${type}-${index}`} {...mergedProps} />;

    // Keep selector full-width while all other blocks stay constrained.
    if (type === "PlayerSelectorSection") {
      return rendered;
    }

    return (
      <container-div key={`container-${type}-${index}`}>
        {rendered}
      </container-div>
    );
  };

  const tabLinks = tabs.map(tab => ({
    label: tab.label,
    link: tab.url
  }));

  return (
    <PageWrap>
      <EventFinished scheduleKeyName={contentKey} eventType="promotion" />
      <Hero
        image={content?.hero?.image}
        bgImage={content?.hero?.bgImage}
        imageAlt={content?.hero?.imageAlt}
        fluidImage={content?.hero?.image ? false : true}
      />
      <NavTabs tabLinks={tabLinks} defaultHash="#vote-win" />

      {tabs.map((tab, index) => {
        if (!tab.url.startsWith('#')) return null;
        const tabKey = tab.url.replace(/^#/, '');
        if (tabKey !== activeTab) return null;

        return (
          <SectionWrap
            key={`content-${index}`}
            variant={tabContent[index]?.variant || "dark"}
            className={tabContent[index]?.variant || "dark"}
            $tabKey={tabKey}
            $activeTab={activeTab}
            $isSecondTab={tabKey === tabKeys[1]}
          >
            {(tabContent[index]?.sections || []).map(
              (section, sectionIndex) =>
                renderSection(section, sectionIndex),
            )}
          </SectionWrap>
        );
      })}
    </PageWrap>
  );
};

const PageWrap = styled.div`
  .creator-form-title-section,
  .creator-cta-section {
    padding: 40px 20px;
  }

  .creator-cta-section p {
    font-size: 1rem;
    line-height: 1.45;
  }

  .creator-cta-section p a {
    font-size: 0.96em;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  @media screen and (max-width: 767px) {
    .creator-cta-section p {
      font-size: 0.92rem;
      line-height: 1.4;
    }
  }
`;

const SectionWrap = styled.div`
  padding-top: 20px;
  background-color: ${(props) => {
    if (props.variant !== "dark") return "#fff";
    if (props.$isSecondTab && props.$activeTab === props.$tabKey) {
      return "var(--black)";
    }
    return "var(--lightblack)";
  }};
  color: ${(props) => (props.variant === "dark" ? "#fff" : "#000")};

  .title-section {
    h2 {
      padding: 0;
      overflow-wrap: break-word;
    }
    p {
      padding: 0;
    }
  }

  h4 {
    margin: 27px 0 5px;
  }
`;

const CreatorFormSection = styled.section`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  .form-container {
    margin: 0 auto;
  }
`;

const CreatorFormWrap = styled.div`
  margin-top: 10px;
`;

const CreatorTableContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  background-color: var(--black);
  padding: 20px;

  .search-input-wrap {
    max-width: 600px;
    margin: 20px auto;
  }

  @media screen and (max-width: 991px) {
    .col {
      margin: 0;
    }

    div {
      margin-top: 0;
    }
  }
`;

const CreatorRules = styled.div`
  p {
    text-align: center;
    color: var(--grey);
    margin: 0 0 10px;
  }
`;

export default WsopcPlaygroundMay2026CreatorChampionship;
